import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Divider,
  Paper,
  Chip,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { UrlApi } from "../../../../../../../../services/apirest";
import { logEventos } from "../../../../../../../../services/services";
import { getData } from "../../../../../../../../services/library";

const formatMoney = (value) =>
  value ? `$${Number(value).toLocaleString("es-AR")}` : "$0";

const AnularRecibosModal = ({
  open,
  onClose,
  showToast,
  idRecibo,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [reciboCompleto, setReciboCompleto] = useState(null);

  useEffect(() => {
    if (idRecibo && open) {
      getData(`/ctacte/recibos/${idRecibo}`, setReciboCompleto);
    }
  }, [idRecibo, open]);

  const handleAnularRecibo = async () => {
    try {
      setLoading(true);

      const response = await fetch(
        `${UrlApi}/ctacte/recibos/anular/${idRecibo}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        },
      );

      if (!response.ok) throw new Error();

      await response.json();

      showToast("El recibo fue anulado con éxito", "success");

      logEventos(
        `El recibo con el ID: ${idRecibo} fue anulado`,
        "Clientes granel",
        "Recibo anulado",
        localStorage.getItem("user"),
      );

      onSuccess?.();
      onClose();
    } catch (error) {
      showToast("Ocurrió un error al anular el recibo", "error");
    } finally {
      setLoading(false);
    }
  };

  const recibo = reciboCompleto?.recibo;
  const imputaciones = reciboCompleto?.imputaciones || [];
  const totales = reciboCompleto?.totales;

  return (
    <Dialog
      open={open}
      onClose={loading ? null : onClose}
      maxWidth="sm"
      fullWidth
    >
      {/* HEADER */}
      <DialogTitle
        sx={{
          bgcolor: "warning.main",
          color: "white",
          py: 2,
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <WarningAmberIcon sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Anular recibo
            </Typography>
            <Typography variant="caption">
              Esta acción es irreversible
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {!reciboCompleto ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Paper sx={{ p: 3, bgcolor: "grey.50" }} elevation={0}>
              <Typography variant="body2" color="text.secondary">
                RECIBO
              </Typography>

              <Typography variant="h6" fontWeight="bold" mb={1}>
                #{String(recibo?.pto_vta).padStart(4, 0) || "-"}-
                {recibo?.numero_recibo || idRecibo}
              </Typography>
              <Chip
                label={`Total: ${formatMoney(totales?.total)}`}
                color="success"
                sx={{ fontWeight: 600 }}
              />
            </Paper>

            <Divider />

            <Box sx={{ p: 3 }}>
              <Typography fontWeight="bold" mb={2}>
                Se desimputarán las siguientes facturas:
              </Typography>

              {imputaciones.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Este recibo no tiene imputaciones.
                </Typography>
              ) : (
                <Box display="flex" flexDirection="column" gap={1.5}>
                  {imputaciones.map((imp) => (
                    <Paper
                      key={imp.id}
                      variant="outlined"
                      sx={{
                        p: 2,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        borderRadius: 2,
                      }}
                    >
                      <Box>
                        <Typography fontWeight={600}>
                          {imp.comprobante || "Factura"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {imp.nro_factura || "-"}
                        </Typography>
                      </Box>

                      <Typography fontWeight={700} color="error.main">
                        {formatMoney(imp.importe)}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              )}
            </Box>

            <Box
              sx={{
                px: 3,
                pb: 2,
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontStyle: "italic" }}
              >
                Al confirmar, el recibo será anulado y las imputaciones
                revertidas en la cuenta corriente.
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={loading} variant="outlined">
          Cancelar
        </Button>

        <Button
          onClick={handleAnularRecibo}
          disabled={loading || !reciboCompleto}
          variant="contained"
          color="warning"
        >
          {loading ? <CircularProgress size={20} /> : "Confirmar anulación"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AnularRecibosModal;
